import React, { useState } from 'react';
import { auth, db } from '../firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { validateEmail, validatePassword, validatePasswordConfirmation, validateName, validatePhone } from '../utils/validationUtils';
import SubscriptionService from '../services/SubscriptionService';
import Modal from './Modal';
import { useTranslation } from '../hooks/useTranslation';

function AddBusinessOwnerModal({ isOpen, onClose, organizationId, organizationCode }) {
  const { t } = useTranslation('admin');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(''); // Clear error when user starts typing
    setFieldErrors(prev => ({ ...prev, [name]: '' })); // Clear field error
    
    // Real-time validation for specific fields
    if (name === 'email' && value) {
      const emailValidation = validateEmail(value);
      if (!emailValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, email: emailValidation.error }));
      }
    }
    
    if (name === 'password' && value) {
      const passwordValidation = validatePassword(value);
      if (!passwordValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, password: passwordValidation.error }));
      }
    }
    
    if (name === 'fullName' && value) {
      const nameValidation = validateName(value, 'Full Name');
      if (!nameValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, fullName: nameValidation.error }));
      }
    }
    
    if (name === 'phoneNumber' && value) {
      const phoneValidation = validatePhone(value);
      if (!phoneValidation.isValid) {
        setFieldErrors(prev => ({ ...prev, phoneNumber: phoneValidation.error }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Comprehensive validation using validationUtils
    const emailValidation = validateEmail(formData.email);
    const passwordValidation = validatePassword(formData.password);
    const passwordConfirmationValidation = validatePasswordConfirmation(formData.password, formData.confirmPassword);
    const nameValidation = validateName(formData.fullName, 'Full Name');
    const phoneValidation = validatePhone(formData.phoneNumber);
    
    // Collect all validation errors
    const newFieldErrors = {};
    let hasErrors = false;
    
    if (!emailValidation.isValid) {
      newFieldErrors.email = emailValidation.error;
      hasErrors = true;
    }
    
    if (!passwordValidation.isValid) {
      newFieldErrors.password = passwordValidation.error;
      hasErrors = true;
    }
    
    if (!passwordConfirmationValidation.isValid) {
      newFieldErrors.confirmPassword = passwordConfirmationValidation.error;
      hasErrors = true;
    }
    
    if (!nameValidation.isValid) {
      newFieldErrors.fullName = nameValidation.error;
      hasErrors = true;
    }
    
    if (!phoneValidation.isValid) {
      newFieldErrors.phoneNumber = phoneValidation.error;
      hasErrors = true;
    }
    
    if (hasErrors) {
      setFieldErrors(newFieldErrors);
      setError(t('addBusinessOwner.errors.fixErrorsBelow'));
      return;
    }

    setLoading(true);

    try {
      // Check user limit before creating Business Owner
      if (!organizationId) {
        setError(t('addBusinessOwner.errors.organizationNotFound'));
        setLoading(false);
        return;
      }
      try {
        const userLimit = await SubscriptionService.checkUserLimit(organizationId);
        if (!userLimit.allowed) {
          setError(t('addBusinessOwner.errors.userLimitReached', { current: userLimit.current, limit: userLimit.limit }));
          setLoading(false);
          return;
        }
      } catch (limitErr) {
        setError(t('addBusinessOwner.errors.failedToCheckUserLimit'));
        setLoading(false);
        return;
      }

      // First create the authentication user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const userData = {
        fullName: formData.fullName,
        email: formData.email,
        phoneNumber: formData.phoneNumber || '',
        role: 'BusinessOwner',
        approved: true,
        createdAt: new Date().toISOString(),
        organizationCode: organizationCode,
        organizationId: organizationId
      };

      // Create user document in the main users collection
      const mainUserDocRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(mainUserDocRef, userData);

      // Create user document in the organization's users subcollection using UID as document ID
      const orgUserDocRef = doc(db, `organizations/${organizationId}/users/${userCredential.user.uid}`);
      await setDoc(orgUserDocRef, userData);

      // Update totalUsers count in organization
      try {
        const { updateOrganizationUserCount } = await import('../services/userCountService');
        await updateOrganizationUserCount(organizationId);
      } catch (err) {
        // Failed to update organization user count - handled silently
      }

      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: ''
      });
      // Sign out the current user and navigate to login
      await auth.signOut();
      onClose();
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError(t('addBusinessOwner.errors.emailAlreadyExists'));
      } else {
        setError(t('addBusinessOwner.errors.failedToAddBusinessOwner'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('addBusinessOwner.title')}>
      <form onSubmit={handleSubmit} className="modal-form">
        <div className="form-group">
          <label htmlFor="fullName">{t('addBusinessOwner.fullName')}</label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            required
            placeholder={t('addBusinessOwner.placeholders.enterFullName')}
            className={fieldErrors.fullName ? 'error' : ''}
          />
          {fieldErrors.fullName && (
            <div className="field-error">{fieldErrors.fullName}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('addBusinessOwner.email')}</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder={t('addBusinessOwner.placeholders.enterEmailAddress')}
            className={fieldErrors.email ? 'error' : ''}
          />
          {fieldErrors.email && (
            <div className="field-error">{fieldErrors.email}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('addBusinessOwner.password')}</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            placeholder={t('addBusinessOwner.placeholders.enterPassword')}
            minLength={6}
            className={fieldErrors.password ? 'error' : ''}
          />
          {fieldErrors.password && (
            <div className="field-error">{fieldErrors.password}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">{t('addBusinessOwner.confirmPassword')}</label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            placeholder={t('addBusinessOwner.placeholders.confirmPassword')}
            minLength={6}
            className={fieldErrors.confirmPassword ? 'error' : ''}
          />
          {fieldErrors.confirmPassword && (
            <div className="field-error">{fieldErrors.confirmPassword}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">{t('addBusinessOwner.phoneNumber')}</label>
          <input
            type="tel"
            id="phoneNumber"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            placeholder={t('addBusinessOwner.placeholders.enterPhoneNumber')}
            className={fieldErrors.phoneNumber ? 'error' : ''}
          />
          {fieldErrors.phoneNumber && (
            <div className="field-error">{fieldErrors.phoneNumber}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('addBusinessOwner.organizationCode')}</label>
          <input
            type="text"
            value={organizationCode}
            disabled
            className="disabled-input"
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="cancel-button">
            {t('addBusinessOwner.cancel')}
          </button>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? t('addBusinessOwner.adding') : t('addBusinessOwner.confirm')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AddBusinessOwnerModal; 